import {useQueries, useQuery} from 'react-query';

import {contextOrganizationId} from 'src/lib/api/api';
import {
  H5ForecastDataRequest,
  H5ObsType,
  fetchH5ForecastData,
  fetchH5ForecastListings,
} from 'src/lib/api/h5';
import {Site} from 'src/lib/api/project';

// Both hooks hand react-query's signal to fetch. H5 reads can take tens of seconds, and a section
// that unmounts (a site change, the preview toggled off) should not leave one running.
//
// contextOrganizationId is in both keys because the request sends it as the
// x-context-organization-id header, so what comes back is per context organization.

export function useH5ForecastListing(site: Site, obsType: H5ObsType, initTimeRange: [Date, Date]) {
  return useQuery(
    [
      'h5Forecasts',
      contextOrganizationId,
      site.id,
      obsType,
      initTimeRange[0].toISOString(),
      initTimeRange[1].toISOString(),
    ],
    ({signal}) => fetchH5ForecastListings(site.id, obsType, initTimeRange, {signal}),
    {retry: false}
  );
}

export type H5ForecastDataRead = {
  request: H5ForecastDataRequest | undefined;
  // The request body carries initialization times only, so the selected issue times have to be
  // part of the cache identity for a re-issued forecast to be fetched again.
  issueTimes: string[];
};

// One query per model, so adding a model to the chart leaves the others' cached reads alone.
export function useH5ForecastData(site: Site, obsType: H5ObsType, reads: H5ForecastDataRead[]) {
  return useQueries(
    reads.map(({request, issueTimes}) => ({
      queryKey: ['h5ForecastData', contextOrganizationId, site.id, obsType, request, issueTimes],
      queryFn: ({signal}: {signal?: AbortSignal}) =>
        fetchH5ForecastData(site.id, obsType, request!, {signal}),
      enabled: !!request,
      retry: false,
    }))
  );
}
